import React, { useMemo } from 'react';
import { ExplorerPanel } from '../explorer/ExplorerPanel';
import { FavoriteFilesPanel } from '../explorer/FavoriteFilesPanel';
import { RecentFilesPanel } from '../explorer/RecentFilesPanel';
import { OutlinePanel } from '../outline/OutlinePanel';
import { SourceControlPanel } from '../git/SourceControlPanel';
import { DependenciesPanel } from '../sidebar/DependenciesPanel';
import { TreeNode, ContextMenuState } from '../../types';
import { useEditorState, useUI } from '../../contexts';
import { useSymbols, Symbol } from '../../hooks/useSymbols';
import { GitFile, GitFileStatus, GitBranch } from '../../hooks/useGit';
import { GitStatusInfo } from '../../components/TreeView';
import { RecentFile } from '../../hooks/useRecentFiles';
import { FavoriteFile } from '../../hooks/useFavoriteFiles';

type SidebarSectionProps = {
  dropProps: {
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  className: string;
  style: React.CSSProperties;
  filteredTree: TreeNode[];
  collapsed: Record<string, boolean>;
  selectedNodeId: string | null;
  multiSelectedIds?: Set<string>;
  treeFilter: string;
  fileTypeFilter: string[];
  availableFileTypes: string[];
  contextMenu: ContextMenuState;
  isDirty: (path: string) => boolean;
  onFilterChange: (val: string) => void;
  onFileTypeFilterChange: (types: string[]) => void;
  onToggleCreateMenu: () => void;
  onCreateDirectory: () => void;
  onCreateFile: () => void;
  onAddProject: () => void;
  onCloseContextMenu: () => void;
  onContextMenu: (e: React.MouseEvent, node: TreeNode) => void;
  onSelectNode: (id: string, event?: React.MouseEvent) => void;
  onOpenFile: (id: string) => void;
  onToggleCollapse: (path: string) => void;
  onRename: () => void;
  onDelete: () => void;
  onNewFile: () => void;
  onNewFolder: () => void;
  onMove?: (sourceId: string, targetId: string) => void;
  onCopy?: () => void;
  onCut?: () => void;
  onPaste?: () => void;
  onDuplicate?: () => void;
  hasClipboard?: boolean;
  onSymbolSelect: (symbol: Symbol) => void;
  // Recent files
  recentFiles?: RecentFile[];
  onRecentFileOpen?: (path: string) => void;
  onRecentFileRemove?: (path: string) => void;
  onRecentFilesClear?: () => void;
  // Favorite files
  favoriteFiles?: FavoriteFile[];
  onFavoriteFileOpen?: (path: string) => void;
  onFavoriteFileRemove?: (path: string) => void;
  onFavoriteFilesClear?: () => void;
  onToggleFavorite?: (path: string) => void;
  isFavorite?: (path: string) => boolean;
  // Git props
  gitFiles: GitFile[];
  gitBranch: string;
  gitBranches: GitBranch[];
  gitAhead: number;
  gitBehind: number;
  gitError: string | null;
  gitStagedFiles: GitFile[];
  gitUnstagedFiles: GitFile[];
  gitCommitMessage: string;
  gitIsCommitting: boolean;
  gitIsPushing: boolean;
  gitIsPulling: boolean;
  gitIsFetching: boolean;
  gitIsSyncing: boolean;
  onGitCommitMessageChange: (message: string) => void;
  onGitStageFile: (path: string) => void;
  onGitUnstageFile: (path: string) => void;
  onGitStageAll: () => void;
  onGitUnstageAll: () => void;
  onGitDiscardFile: (path: string) => void;
  onGitCommit: () => void;
  onGitPush: () => void;
  onGitPull: () => void;
  onGitFetch: () => void;
  onGitSync: () => void;
  onGitSwitchBranch: (branch: string) => void;
  onGitCreateBranch: (name: string) => void;
  onGitDeleteBranch: (name: string) => void;
  onGitOpenFile: (path: string) => void;
  onGitShowDiff: (path: string) => void;
  getGitStatusIcon: (status: GitFileStatus) => string;
  getGitStatusColor: (status: GitFileStatus) => string;
  // Dependencies panel
  workingFiles?: Record<string, string>;
};

export const SidebarSection = React.memo(function SidebarSection({
  dropProps,
  className,
  style,
  filteredTree,
  collapsed,
  selectedNodeId,
  multiSelectedIds,
  treeFilter,
  fileTypeFilter,
  availableFileTypes,
  contextMenu,
  isDirty,
  onFilterChange,
  onFileTypeFilterChange,
  onToggleCreateMenu,
  onCreateDirectory,
  onCreateFile,
  onAddProject,
  onCloseContextMenu,
  onContextMenu,
  onSelectNode,
  onOpenFile,
  onToggleCollapse,
  onRename,
  onDelete,
  onNewFile,
  onNewFolder,
  onMove,
  onCopy,
  onCut,
  onPaste,
  onDuplicate,
  hasClipboard,
  onSymbolSelect,
  // Recent files
  recentFiles,
  onRecentFileOpen,
  onRecentFileRemove,
  onRecentFilesClear,
  // Favorite files
  favoriteFiles,
  onFavoriteFileOpen,
  onFavoriteFileRemove,
  onFavoriteFilesClear,
  onToggleFavorite,
  isFavorite,
  // Git props
  gitFiles,
  gitBranch,
  gitBranches,
  gitAhead,
  gitBehind,
  gitError,
  gitStagedFiles,
  gitUnstagedFiles,
  gitCommitMessage,
  gitIsCommitting,
  gitIsPushing,
  gitIsPulling,
  gitIsFetching,
  gitIsSyncing,
  onGitCommitMessageChange,
  onGitStageFile,
  onGitUnstageFile,
  onGitStageAll,
  onGitUnstageAll,
  onGitDiscardFile,
  onGitCommit,
  onGitPush,
  onGitPull,
  onGitFetch,
  onGitSync,
  onGitSwitchBranch,
  onGitCreateBranch,
  onGitDeleteBranch,
  onGitOpenFile,
  onGitShowDiff,
  getGitStatusIcon,
  getGitStatusColor,
  workingFiles,
}: SidebarSectionProps) {
  const { activeFile, code } = useEditorState();
  const { showCreateMenu } = useUI();

  // Get symbols from current code
  const { symbols } = useSymbols(code);

  // Build git status map for tree view
  const gitStatus = useMemo(() => {
    const statusMap: Record<string, GitStatusInfo> = {};
    for (const file of gitFiles) {
      statusMap[file.path] = {
        icon: getGitStatusIcon(file.status),
        color: getGitStatusColor(file.status),
      };
    }
    return statusMap;
  }, [gitFiles, getGitStatusIcon, getGitStatusColor]);

  return (
    <aside
      {...dropProps}
      className={className}
      style={style}
      aria-label="File explorer sidebar"
    >
      <ExplorerPanel
        filteredTree={filteredTree}
        collapsed={collapsed}
        activeFile={activeFile}
        selectedNodeId={selectedNodeId}
        multiSelectedIds={multiSelectedIds}
        treeFilter={treeFilter}
        fileTypeFilter={fileTypeFilter}
        availableFileTypes={availableFileTypes}
        showCreateMenu={showCreateMenu}
        contextMenu={contextMenu}
        isDirty={isDirty}
        gitStatus={gitStatus}
        onFilterChange={onFilterChange}
        onFileTypeFilterChange={onFileTypeFilterChange}
        onToggleCreateMenu={onToggleCreateMenu}
        onCreateDirectory={onCreateDirectory}
        onCreateFile={onCreateFile}
        onAddProject={onAddProject}
        onCloseContextMenu={onCloseContextMenu}
        onContextMenu={onContextMenu}
        onSelectNode={onSelectNode}
        onOpenFile={onOpenFile}
        onToggleCollapse={onToggleCollapse}
        onRename={onRename}
        onDelete={onDelete}
        onNewFile={onNewFile}
        onNewFolder={onNewFolder}
        onMove={onMove}
        onCopy={onCopy}
        onCut={onCut}
        onPaste={onPaste}
        onDuplicate={onDuplicate}
        hasClipboard={hasClipboard}
        onToggleFavorite={onToggleFavorite && contextMenu.node ? () => onToggleFavorite(contextMenu.node!.path) : undefined}
        isFavorite={isFavorite && contextMenu.node ? isFavorite(contextMenu.node.path) : false}
      />
      {favoriteFiles && favoriteFiles.length > 0 && onFavoriteFileOpen && (
        <FavoriteFilesPanel
          favoriteFiles={favoriteFiles}
          activeFile={activeFile}
          onOpenFile={onFavoriteFileOpen}
          onRemoveFile={onFavoriteFileRemove || (() => {})}
          onClearAll={onFavoriteFilesClear || (() => {})}
        />
      )}
      {recentFiles && recentFiles.length > 0 && onRecentFileOpen && (
        <RecentFilesPanel
          recentFiles={recentFiles}
          activeFile={activeFile}
          onOpenFile={onRecentFileOpen}
          onRemoveFile={onRecentFileRemove || (() => {})}
          onClearAll={onRecentFilesClear || (() => {})}
        />
      )}
      {activeFile && (
        <OutlinePanel
          symbols={symbols}
          onSelect={onSymbolSelect}
        />
      )}
      <SourceControlPanel
        branch={gitBranch}
        branches={gitBranches}
        ahead={gitAhead}
        behind={gitBehind}
        error={gitError}
        stagedFiles={gitStagedFiles}
        unstagedFiles={gitUnstagedFiles}
        commitMessage={gitCommitMessage}
        isCommitting={gitIsCommitting}
        isPushing={gitIsPushing}
        isPulling={gitIsPulling}
        isFetching={gitIsFetching}
        isSyncing={gitIsSyncing}
        onCommitMessageChange={onGitCommitMessageChange}
        onStageFile={onGitStageFile}
        onUnstageFile={onGitUnstageFile}
        onStageAll={onGitStageAll}
        onUnstageAll={onGitUnstageAll}
        onDiscardFile={onGitDiscardFile}
        onCommit={onGitCommit}
        onPush={onGitPush}
        onPull={onGitPull}
        onFetch={onGitFetch}
        onSync={onGitSync}
        onSwitchBranch={onGitSwitchBranch}
        onCreateBranch={onGitCreateBranch}
        onDeleteBranch={onGitDeleteBranch}
        onOpenFile={onGitOpenFile}
        onShowDiff={onGitShowDiff}
        getStatusIcon={getGitStatusIcon}
        getStatusColor={getGitStatusColor}
      />
      {workingFiles && (
        <DependenciesPanel
          files={workingFiles}
          onOpenFile={onGitOpenFile}
        />
      )}
    </aside>
  );
});
